import {
  betStore,
  BetStatus,
  popupStore,
  RoundStatus,
  freeBetStore,
  userInfoStore,
  roundInfoStore,
  BetResponseCode,
  gameResultStore,
  gameConfigStore,
  applicationStore,
  userSettingsStore,
  notificationStore,
  FreeBetWinningType,
} from '@pascal/pascal-core';

import {
  betActionName,
  defaultJetIndex,
  autoBetDataKeys,
  autoCashOutDataKeys,
} from '../enums/uiConfig';
import { worker } from '../index';
import { createGuid } from '../helpers';
import { liveConnection } from '../core';
import { gameLiveConnection } from './GameConnection';
import { soundService, soundTypes } from './soundService';
import { doBetStore, gameStore, loadingStore } from '../store';
import { buildCurrentLiveBets } from '../helpers/buildCurrentLiveBets';
import { divide, minus, multiply, toFixed } from '../helpers/arithmeticOperations';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    limits,
    results,
    roundInfo,
    currentBets,
    userFreebets,
    underMaintenance,
  }) {
    const { OnHold, Started, OnFinish, Finished } = RoundStatus;
    const {
      status,
      second,
      jets: { crashedJets },
    } = roundInfo;

    betStore.setLiveBets([]);
    gameStore.setOddData(null);
    gameStore.updatedIsTabChange(false);
    doBetStore.resetGameStates();
    gameStore.setCurrentJetsInfo();
    gameStore.setFighterBlowAnim();
    gameStore.updatedBetCountData();
    gameStore.setCurrentRoundStatus();
    freeBetStore.resetActiveFreeBet();
    freeBetStore.resetExtraFreeBetData();
    gameStore.setInitialPassedSecond();
    gameStore.setIsAnimationOffOn(true);
    doBetStore.checkMaxWinFunctional();

    if (second <= 3.4 && status === Started) {
      gameStore.setInitialPassedSecond(second * 1000);
      gameStore.setCurrentRoundStatus(RoundStatus.Started);
    } else if (status === OnHold) {
      gameStore.setInitialPassedSecond(-second * 1000);
      gameStore.setCurrentRoundStatus(RoundStatus.OnHold);
    }

    doBetStore.setInitialBetAmount(limits?.defaultBetAmount || limits?.minBet);

    if (underMaintenance) {
      const { startDate, notifySeconds } = underMaintenance;
      popupStore.startUnderMaintenanceFunctional({ startDate, notifySeconds });
    }

    gameStore.setGameState({
      status,
      isInitialLoaded: true,
    });

    if (status === Started) {
      const isBetClosed = second < 0;
      if (isBetClosed) {
        gameStore.setIsBetsClosed(true);
      }
      doBetStore.setWinData();
      betStore.setLiveBets([]);
    } else if (status === OnFinish || status === Finished) {
      gameStore.setInitialPassedSecond();
      let currentWinData;
      if (currentBets && currentBets.length) {
        currentWinData = currentBets.filter(
          (item) => item?.userId === Number(userInfoStore.userId)
        );

        if (currentWinData.length) {
          currentWinData.forEach((item) =>
            doBetStore.setWinData(item.winAmount || item.halfWinAmount, item.betData.index)
          );
        }
      }
    }
    if (status === OnHold) {
      gameStore.setCurrentRoundStatus(RoundStatus.OnHold);
      gameStore.setIsBetsClosed(true);
    }

    doBetStore.updateAutoCashOutData(
      autoCashOutDataKeys.autoCashOutMaxOdd,
      toFixed(divide(limits.maxWin, limits?.defaultBetAmount || limits?.minBet)),
      defaultJetIndex
    );

    if (currentBets && currentBets.length) {
      currentBets.forEach((bet) => {
        gameStore.updatedBetCountData(betActionName.realBetsCount);
        if (bet.status === BetStatus.Win) {
          gameStore.updatedBetCountData(betActionName.realBetsCashOutCount);
        }
      });
      betStore.setLiveBets(buildCurrentLiveBets(Number(userInfoStore?.userId), currentBets));
      if (!applicationStore.isDemo) {
        const userCurrentBets = currentBets.filter(
          (item) => item?.userId === Number(userInfoStore.userId)
        );

        userCurrentBets.forEach((item) => {
          doBetStore.updateInitialBetData(item, item.betData.index, userFreebets);
          gameStore.setActiveCurrentSection(item.betData.index);
          if (item.freeBetId) {
            const currentFreeBetData = userFreebets.find(
              (freeBet) => freeBet.id === item.freeBetId
            );
            doBetStore.setBetAmount(currentFreeBetData?.amount);
            doBetStore.setFreeBetData(
              currentFreeBetData.minOdd,
              item.freeBetId,
              currentFreeBetData.maxPayout,
              currentFreeBetData?.winType,
              item.betData.index
            );
          }
        });
      }
    }

    if (crashedJets?.length) {
      crashedJets.forEach((jetInfo) => doBetStore.setFighterFinishedOdd(jetInfo));
      gameStore.setFighterBlowAnim(true);
    }

    gameResultStore.setGameResults(results);
    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);

    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      gameStore.setTimerDuration(roundInfo.second);
      const time = Math.floor(Math.abs(roundInfo.second));
      if (userSettingsStore.isAnimationOn) {
        soundService.play(soundTypes.JetStart, second);
      }
      soundService.play(soundTypes.Timer, second);
      gameStore.setTime(time);
      this.sendWorker(time);
    } else {
      this.sendWorker();
      gameStore.setTimerDuration(roundInfo.duration);
      gameStore.setTime(null);
    }

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { isSoundOn } = userSettingsStore;
    if (Number(userId) === Number(+data.userId)) {
      doBetStore.handleDoBetResult(data);
      doBetStore.updateIsDisabledBetSection(false, data.betData.index);

      if (isSoundOn) {
        soundService.playBetAccepted();
      }
    }
    betStore.updateLiveBets(data);
    gameStore.updatedBetCountData(betActionName.realBetsCount);
  }

  handleDoBetResultValidation(data) {
    const {
      responseCode,
      betData: { index },
    } = data;
    const autoBetData = doBetStore.autoBetData.find((el) => el.fighterNumber === index);
    doBetStore.updateIsDisabledBetSection(false, index);
    if (autoBetData.isAutoBet && responseCode === BetResponseCode.InsufficientFunds) {
      doBetStore.updateAutoBetData(autoBetDataKeys.isAutoBet, false, index);
      doBetStore.updateAutoBetData(autoBetDataKeys.autoBetCount, 0, index);
      doBetStore.updateAutoBetData(autoBetDataKeys.isOpened, false, index);
    }
    notificationStore.setNotificationType({ responseCode });
  }

  handleRoundStart({ data: { startedRound, results } }) {
    const { roundId } = startedRound;

    doBetStore.handleClearRoundData();
    betStore.setLiveBets([]);
    roundInfoStore.setRoundId(roundId);
    doBetStore.handleOnRoundStart();
    soundService.play(soundTypes.Timer);
    if (userSettingsStore.isAnimationOn) {
      soundService.play(soundTypes.JetStart);
    }

    this.sendWorker(roundInfoStore.duration);
    gameStore.setGameState({
      status: RoundStatus.Started,
      isInitialLoaded: false,
    });

    // gameLiveConnection.getBalance();
    doBetStore.setWinData();

    gameResultStore.setGameResults(results);
  }

  handleResultReport({ data: { resultReportData } }) {
    const {
      seconds,
      firstBets,
      thirdBets,
      secondBets,
      currentOdd,
      crashedItemsInfo,
      currentItemsInfo,
    } = resultReportData;
    const { isFirst } = doBetStore.activeMaxPayoutPopData;
    const { betData, autoBetData, autoCashOutData } = doBetStore;
    const { repliedBetData } = betStore;
    const { isDemo } = applicationStore;

    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });

    currentItemsInfo.forEach((jetInfo) => gameStore.setCurrentJetsInfo(jetInfo));
    gameStore.setOddData(currentOdd);

    if (gameStore.currentRoundStatus !== RoundStatus.OnHold) {
      gameStore.setCurrentRoundStatus(RoundStatus.OnHold);
    }

    if (gameStore.isAnimationOffOn && currentOdd === 1 && userSettingsStore.isAnimationOn) {
      gameStore.setIsAnimationOffOn();
    }

    if (gameStore.currentRoundPassedSecond && gameStore.isTabChange) {
      gameStore.updatedIsTabChange(false);
    }

    if (gameStore.isTabChange || (!userSettingsStore.isAnimationOn && gameStore.isAnimationOffOn)) {
      gameStore.setCurrentRoundPassedSecond(-seconds * 1000);
    }

    betData.forEach((item) => {
      const {
        betId,
        amount,
        freeBetId,
        isDisabled,
        freeBetType,
        finishedOdd,
        isBetAccepted,
        fighterNumber,
        freeBetMaxPayout = null,
      } = item;

      const freeBetAmount =
        freeBetId && freeBetType === FreeBetWinningType.Win
          ? toFixed(
              minus(
                Number(
                  toFixed(
                    multiply(currentItemsInfo[fighterNumber]?.currentOdd || 0, Number(amount)),
                    2
                  )
                ),
                Number(amount) || 0
              ) || 0,
              2
            )
          : toFixed(
              multiply(currentItemsInfo[fighterNumber]?.currentOdd || 0, Number(amount || 0)) || 0,
              2
            );

      if (freeBetId && isBetAccepted && freeBetMaxPayout && freeBetAmount > freeBetMaxPayout) {
        doBetStore.setActiveMaxPayoutPopUpData(true, freeBetMaxPayout, isFirst);
      }

      if (isBetAccepted && gameStore.isBetsClosed && isDisabled) {
        doBetStore.updateIsDisabledBetSection(false, fighterNumber);
      }

      const fighterAutoBetData = autoBetData.find((el) => el.fighterNumber === fighterNumber);
      const fighterAutoCashOutData = autoCashOutData.find(
        (el) => el.fighterNumber === fighterNumber
      );

      if (isDemo && isDisabled && !fighterAutoBetData.isAutoBet) {
        doBetStore.updateIsDisabledBetSection(false, fighterNumber);
      }

      if (
        isDemo &&
        fighterAutoCashOutData.isOpened &&
        currentItemsInfo[fighterNumber]?.currentOdd ===
          Number(fighterAutoCashOutData.autoCashOutOdd) &&
        isBetAccepted &&
        !finishedOdd
      ) {
        gameStore.updatedBetCountData(betActionName.demoBetsCashOutCount);
        doBetStore.sendCashOut(betId, fighterNumber);
      }
    });

    if (!gameStore.isBetsClosed) {
      gameStore.setIsBetsClosed(true);
    }

    if (crashedItemsInfo?.length) {
      crashedItemsInfo.forEach((jetInfo) => {
        doBetStore.setFighterFinishedOdd(jetInfo);
        soundService.play(soundTypes.Blowup);
        if (freeBetStore.extraFreeBetData[jetInfo.index].isShowPopUp) {
          freeBetStore.resetExtraFreeBetData(jetInfo.index);
        }
      });
      gameStore.setFighterBlowAnim();
    }

    const winData = [];

    if (firstBets?.length) {
      firstBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
      const firstWinData = firstBets.find((item) => Number(userInfoStore.userId) === item.userId);
      if (firstWinData) {
        winData.push(firstWinData);
      }
    }

    if (secondBets?.length) {
      secondBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
      const secondWinData = secondBets.find((item) => Number(userInfoStore.userId) === item.userId);
      if (secondWinData) {
        winData.push(secondWinData);
      }
    }

    if (thirdBets?.length) {
      thirdBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
      const thirdWinData = thirdBets.find((item) => Number(userInfoStore.userId) === item.userId);
      if (thirdWinData) {
        winData.push(thirdWinData);
      }
    }

    if (
      repliedBetData &&
      currentOdd &&
      currentOdd === repliedBetData.additionalParams?.cashOutInfos[0].odd
    ) {
      doBetStore.sendCashOut(repliedBetData.id, repliedBetData.additionalParams.index, true);
      this.handleCashOutResult({
        ...repliedBetData,
        status: betStore.liveBets[0].status,
        cashOutHalf: true,
      });
    }

    if (winData?.length) {
      let filterMyWinBets;
      winData.forEach((itemWindData) => {
        filterMyWinBets = betStore.liveBets
          .filter((item) => item.userId === itemWindData.userId)
          .map((item) => {
            if (item.status === BetStatus.Pending && item.id === itemWindData.id) {
              return {
                ...item,
                status: BetStatus.Win,
                odd: itemWindData.odd,
                winAmount: itemWindData?.additionalParams?.cashOutInfos.length
                  ? itemWindData?.additionalParams?.cashOutInfos[1].winAmount
                  : itemWindData.winAmount,
              };
            }
            return item;
          });
        betStore.setLiveBets([
          ...(filterMyWinBets?.length ? filterMyWinBets : []),
          ...(betStore.liveBets || []).filter(
            (item) => item.userId !== Number(userInfoStore.userId)
          ),
        ]);
        soundService.playWin();
        doBetStore.setWinData(itemWindData.winAmount, itemWindData.additionalParams.index);
        setTimeout(gameLiveConnection.getBalance, 500);
        const cashOutData = autoCashOutData.find(
          (el) => el.fighterNumber === itemWindData.additionalParams.index
        );
        const currentAutoBetData = autoBetData.find(
          (el) => el.fighterNumber === itemWindData.additionalParams.index
        );
        if (
          cashOutData.autoCashOutOdd &&
          cashOutData.isOpened &&
          currentAutoBetData.isAutoBet &&
          currentAutoBetData.autoBetCount
        ) {
          doBetStore.updateIsDisabledBetSection(true, itemWindData.additionalParams.index);
        }
        if (
          cashOutData.autoCashOutOdd &&
          cashOutData.isOpened &&
          currentAutoBetData.isAutoBet &&
          !currentAutoBetData.autoBetCount
        ) {
          doBetStore.resetAutoBet();
        }
      });
    }

    if (firstBets?.length || secondBets?.length || thirdBets?.length) {
      const otherUserBets = [
        ...(firstBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
        ...(secondBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
        ...(thirdBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
      ];

      if (otherUserBets?.length) {
        const otherUserUpdatedBets = betStore.liveBets.map((item) => {
          const otherUserBet = otherUserBets.find((bet) => bet.id === item.id);
          return otherUserBet || item;
        });
        betStore.setLiveBets(otherUserUpdatedBets);
      }
    }

    if (!currentItemsInfo.length) {
      const updatedBets = betStore.liveBets.map((bet) => {
        return {
          ...bet,
          odd: bet.status === BetStatus.Pending ? 0 : bet.odd,
          status: bet.status === BetStatus.Pending ? BetStatus.Lose : bet.status,
        };
      });

      betStore.setLiveBets(updatedBets);
    }

    const myBetsData = doBetStore.betData.filter((item) => item.betId && !item.winAmount);
    if (myBetsData?.length) {
      doBetStore.checkMaxWinFunctional(myBetsData);
    }
  }

  // handleAutoCashOutResult({ data: { resultReportData } }) {
  //   const winData = [];
  //   const { firstBets, thirdBets, secondBets, currentOdd } = resultReportData;
  //   const { autoBetData, autoCashOutData } = doBetStore;
  //
  //   const { repliedBetData } = betStore;
  //
  //   if (firstBets?.length) {
  //     firstBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
  //     const firstWinData = firstBets.find((item) => Number(userInfoStore.userId) === item.userId);
  //     if (firstWinData) {
  //       winData.push(firstWinData);
  //     }
  //   }
  //
  //   if (secondBets?.length) {
  //     secondBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
  //     const secondWinData = secondBets.find((item) => Number(userInfoStore.userId) === item.userId);
  //     if (secondWinData) {
  //       winData.push(secondWinData);
  //     }
  //   }
  //
  //   if (thirdBets?.length) {
  //     thirdBets.forEach(() => gameStore.updatedBetCountData(betActionName.realBetsCashOutCount));
  //     const thirdWinData = thirdBets.find((item) => Number(userInfoStore.userId) === item.userId);
  //     if (thirdWinData) {
  //       winData.push(thirdWinData);
  //     }
  //   }
  //
  //   if (
  //     repliedBetData &&
  //     currentOdd &&
  //     currentOdd === repliedBetData.additionalParams?.cashOutInfos[0].odd
  //   ) {
  //     doBetStore.sendCashOut(repliedBetData.id, repliedBetData.additionalParams.index, true);
  //     this.handleCashOutResult({
  //       ...repliedBetData,
  //       status: betStore.liveBets[0].status,
  //       cashOutHalf: true,
  //     });
  //   }
  //
  //   if (winData?.length) {
  //     let filterMyWinBets;
  //     winData.forEach((itemWindData) => {
  //       filterMyWinBets = betStore.liveBets
  //         .filter((item) => item.userId === itemWindData.userId)
  //         .map((item) => {
  //           if (item.status === BetStatus.Pending && item.id === itemWindData.id) {
  //             return {
  //               ...item,
  //               status: BetStatus.Win,
  //               odd: itemWindData.odd,
  //               winAmount: itemWindData?.additionalParams?.cashOutInfos.length
  //                 ? itemWindData?.additionalParams?.cashOutInfos[1].winAmount
  //                 : itemWindData.winAmount,
  //             };
  //           }
  //           return item;
  //         });
  //       soundService.playWin();
  //       doBetStore.setWinData(itemWindData.winAmount, itemWindData.additionalParams.index);
  //       setTimeout(gameLiveConnection.getBalance, 500);
  //       const cashOutData = autoCashOutData.find(
  //         (el) => el.fighterNumber === itemWindData.additionalParams.index
  //       );
  //       const currentAutoBetData = autoBetData.find(
  //         (el) => el.fighterNumber === itemWindData.additionalParams.index
  //       );
  //       if (
  //         cashOutData.autoCashOutOdd &&
  //         cashOutData.isOpened &&
  //         currentAutoBetData.isAutoBet &&
  //         currentAutoBetData.autoBetCount
  //       ) {
  //         doBetStore.updateIsDisabledBetSection(true, itemWindData.additionalParams.index);
  //       }
  //       if (
  //         cashOutData.autoCashOutOdd &&
  //         cashOutData.isOpened &&
  //         currentAutoBetData.isAutoBet &&
  //         !currentAutoBetData.autoBetCount
  //       ) {
  //         doBetStore.resetAutoBet();
  //       }
  //     });
  //
  //     betStore.setLiveBets([
  //       ...(filterMyWinBets?.length ? filterMyWinBets : []),
  //       ...(betStore.liveBets || []).filter((item) => item.userId !== Number(userInfoStore.userId)),
  //     ]);
  //   }
  //
  //   if (firstBets?.length || secondBets?.length || thirdBets?.length) {
  //     const otherUserBets = [
  //       ...(firstBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
  //       ...(secondBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
  //       ...(thirdBets?.filter((item) => Number(userInfoStore.userId) !== item.userId) || []),
  //     ];
  //
  //     if (otherUserBets?.length) {
  //       const otherUserUpdatedBets = betStore.liveBets.map((item) => {
  //         const otherUserBet = otherUserBets.find((bet) => bet.id === item.id);
  //         return otherUserBet || item;
  //       });
  //       betStore.setLiveBets(otherUserUpdatedBets);
  //     }
  //   }
  // }

  handleRoundFin() {
    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });

    doBetStore.resetActiveMaxPayoutPopUpData(true);

    gameStore.setIsRoundFin(true);
    doBetStore.checkMaxWinFunctional();

    gameStore.setTimerDuration(roundInfoStore.duration);
  }

  handleBetsClosed() {
    const { isDemo } = applicationStore;

    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });

    gameStore.setIsBetsClosed(true);
    doBetStore.updatedRoundData();

    if (isDemo) {
      doBetStore.updateIsDisabledBetSection(true);
    }
  }

  handleCashOutResult(cashOutData) {
    const { isDemo } = applicationStore;
    const { userId: currentUserId } = userInfoStore;
    const autoBetData = doBetStore.autoBetData.find(
      (el) => el.fighterNumber === cashOutData?.additionalParams?.index
    );

    if (
      autoBetData?.isAutoBet &&
      autoBetData?.autoBetCount &&
      (cashOutData.userId === Number(currentUserId) || isDemo)
    ) {
      doBetStore.updateIsDisabledBetSection(true, cashOutData?.additionalParams.index);
    }
    doBetStore.resetActiveMaxPayoutPopUpData(true);
    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });

    if (cashOutData?.userId === Number(currentUserId)) {
      if (cashOutData.status === BetStatus.Win) {
        gameStore.setIsShowMaxWinPopUpData(false, cashOutData?.additionalParams.index);
        if (freeBetStore.extraFreeBetData[cashOutData?.additionalParams.index].isShowPopUp) {
          freeBetStore.resetExtraFreeBetData(cashOutData?.additionalParams.index);
        }
        gameStore.updatedBetCountData(betActionName.realBetsCashOutCount);
        soundService.playWin();
        doBetStore.setWinData(cashOutData.winAmount, cashOutData?.additionalParams.index);
        const myBets = betStore?.liveBets
          ?.filter((item) => Number(item?.userId) === Number(userInfoStore.userId))
          .reduce((acc, curr) => {
            if (Number(curr.id) === Number(cashOutData?.id)) {
              if (cashOutData?.cashOutHalf) {
                return [
                  ...acc.filter((item) => item?.id !== curr?.id),
                  {
                    ...curr,
                    odd: cashOutData.odd,
                    status: BetStatus.Win,
                    ...cashOutData?.additionalParams?.cashOutInfos[1],
                  },
                ];
              }
              return [...acc, { ...curr, ...cashOutData }];
            }

            return [...acc, curr];
          }, []);

        betStore.setLiveBets([
          ...myBets,
          ...(betStore.liveBets || []).filter(
            (item) => item.userId !== Number(userInfoStore.userId)
          ),
        ]);
      }

      if (cashOutData.status === BetStatus.Pending) {
        const myBets = betStore?.liveBets
          ?.filter((item) => Number(item?.userId) === Number(userInfoStore.userId))
          .reduce((acc, curr) => {
            if (Number(curr.id) === Number(cashOutData?.id) && cashOutData?.cashOutHalf) {
              doBetStore.setWinData(
                cashOutData?.additionalParams?.cashOutInfos[0].winAmount,
                cashOutData?.additionalParams.index,
                true
              );

              return [
                ...acc.filter((item) => item?.id !== curr?.id),
                {
                  ...curr,
                  amount: divide(curr?.changedAmount ? curr?.changedAmount : curr?.amount, 2),
                  status: BetStatus.Pending,
                  odd: 0,
                  winAmount: 0,
                },
                {
                  ...curr,
                  id: createGuid(),
                  amount: divide(curr?.amount, 2),
                  status: BetStatus.Win,
                  odd: cashOutData.odd,
                  winAmount: cashOutData?.additionalParams?.cashOutInfos[0].winAmount,
                },
              ];
            }
            return [...acc, curr];
          }, []);

        betStore.setLiveBets([
          ...myBets,
          ...(betStore.liveBets || []).filter(
            (item) => item.userId !== Number(userInfoStore.userId)
          ),
        ]);
      }
    } else {
      const liveBetsData = betStore?.liveBets
        .map((item) => {
          const otherUsersCashOut = [cashOutData].find(
            (data) => data.id === item.id && data.userId !== +userInfoStore.userId
          );
          return otherUsersCashOut
            ? {
                ...otherUsersCashOut,
                ...(otherUsersCashOut?.changedAmount
                  ? {}
                  : {
                      changedAmount: otherUsersCashOut?.amount,
                      changedCoinAmount: otherUsersCashOut?.coinAmount,
                    }),
              }
            : item;
        })
        ?.reduce((acc, curr) => {
          if (
            curr?.status === BetStatus?.Pending &&
            Number(curr.id) === Number(cashOutData?.id) &&
            curr?.cashOutHalf &&
            curr?.userId !== Number(userInfoStore.userId)
          ) {
            return [
              ...acc.filter((item) => item?.id !== curr?.id),
              {
                ...curr,
                amount: divide(curr?.changedAmount ? curr?.changedAmount : curr?.amount, 2),
                coinAmount: divide(
                  curr?.changedCoinAmount ? curr?.changedCoinAmount : curr?.coinAmount,
                  2
                ),
                status: BetStatus?.Pending,
                odd: 0,
                winAmount: 0,
              },
              {
                ...curr,
                ...curr?.additionalParams?.cashOutInfos[0],
                id: createGuid(),
                odd: cashOutData.odd,
                status: BetStatus.Win,
              },
            ];
          }

          if (
            Number(curr.id) === Number(cashOutData?.id) &&
            curr?.status === BetStatus?.Win &&
            cashOutData?.cashOutHalf
          ) {
            return [
              ...acc.filter((item) => item?.id !== curr?.id),
              {
                ...curr,
                ...cashOutData?.additionalParams?.cashOutInfos[1],
                odd: cashOutData.odd,
                status: BetStatus.Win,
              },
            ];
          }
          return [...acc, curr];
        }, []);

      betStore.setLiveBets(liveBetsData);
    }
  }

  countDown({ data: time }) {
    gameStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then(() => {
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
